import { useEffect, useState } from 'react';
import { useSignalrConnection } from 'hooks';
import { Button, DialogContent, Typography, useMediaQuery, DialogTitle } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { useSnackbar } from 'notistack';
import { useAppDispatch } from 'store';
import { ModalTitle } from 'components';
import { HubPatient } from 'types';
import styles from './RequeueDialog.module.scss';

interface RequeueDialogProps extends DialogProps {
  patient: HubPatient | null;
  onClose: () => void;
}

export function RequeueDialog({ open, patient, onClose, ...rest }: RequeueDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { events } = useSignalrConnection();
  const isMobile = useMediaQuery('(max-width:600px)');

  const [currentRoomUrl, setCurrentRoomUrl] = useState<string>('');

  useEffect(() => {
    if (open) {
      const input = document.querySelector<HTMLInputElement>('input.MuiInputBase-input');
      const visibleRoomName = input?.value?.trim();
      const origin = window.location.origin;
      const roomUrl = visibleRoomName ? `${origin}/${visibleRoomName}` : '';
      setCurrentRoomUrl(roomUrl);
    }
  }, [open]);

  const handleRequeue = async () => {
    if (!patient || !currentRoomUrl) {
      console.error('❌ Requeue failed: Missing patient or currentRoomUrl');
      return;
    }

    console.log('📤 Requeue Request Data:', {
      PatientConnectionId: patient.connectionId,
      NewRoomUrl: currentRoomUrl,
    });

    try {
      const response = await fetch(`${process.env['REACT_APP_HIPAA_LINK_API_URI']}api/transfer/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          PatientConnectionId: patient.connectionId,
          NewRoomUrl: currentRoomUrl,
        }),
      });

      if (response.ok) {
        console.log('✅ Requeue request sent successfully.');
        enqueueSnackbar('Client requeued successfully!', { variant: 'success' });
        onClose();
      } else {
        const errorText = await response.text();
        console.error('❌ Requeue API error:', errorText);
        enqueueSnackbar('Failed to requeue client.', { variant: 'error' });
      }
    } catch (err) {
      console.error('❌ Error during requeue:', err);
      enqueueSnackbar('Requeue failed due to network error.', { variant: 'error' });
    }
  };

  return (
    <Dialog {...rest} open={open} className={styles.dialog}>
      <DialogTitle>
        <ModalTitle title='Requeue' onClose={onClose} />
      </DialogTitle>
      <DialogContent className={styles.scrollableContent}>
        <Typography component='p' variant='body2' style={{ marginBottom: '.5rem' }}>
          Send this participant back to the waiting room?
        </Typography>

        <div className={styles.buttonContainer}>
          <Button variant='outlined' fullWidth className={styles.cancelButton} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='contained'
            fullWidth
            className={styles.transferButton}
            onClick={handleRequeue}
            disabled={!currentRoomUrl}
          >
            {isMobile ? 'Requeue' : 'Yes, Requeue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
